import React, {forwardRef, useImperativeHandle, useRef} from "react";
import {EditorContent, ReactNodeViewRenderer, useEditor} from "@tiptap/react";
import {FROM_JOURNAL} from "../../Const";
import {getCardById, getCardDoc, updateCard} from "@model/card";
import {imgServer} from "@/components/EditorV2/imgServer";
import {updateNoteDoc} from "@model/note";
import StarterKit from "@tiptap/starter-kit";
import {Import} from "@tiptap-pro/extension-import";
import {getAllHighlightContent} from "@/Utility0";
import Highlight from "@tiptap/extension-highlight";
import {getImagePathWithThemeSvg, imgPathPerfix, sleep} from "@/utils";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import {Export} from "@tiptap-pro/extension-export";
import Focus from "@tiptap/extension-focus";
import RenderCustomTable from "@/components/EditorV2/Table/RenderCustomTable";
import {TableRow} from "@tiptap/extension-table-row";
import {TableHeader} from "@tiptap/extension-table-header";
import CustomTableCell from "@/components/EditorV2/TableCell/tableCell";
import Youtube from "@tiptap/extension-youtube";
import ListKeymap from "@tiptap/extension-list-keymap";
import HardBreak from "@tiptap/extension-hard-break";
import latexBlock from "@/components/EditorV2/latexMath/latexBlock";
import {UniqueID} from "@tiptap-pro/extension-unique-id";
import Gapcursor from "@tiptap/extension-gapcursor";
import Text from "@tiptap/extension-text";
import TextStyle from "@tiptap/extension-text-style";
import {Color} from "@tiptap/extension-color";
import Heading from "@tiptap/extension-heading";
import {Plugin, PluginKey} from "prosemirror-state";
import Paragraph from "@tiptap/extension-paragraph";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import {getBlockMenu} from "@/components/EditorV2/BlockMenu/BlockMenu";
import {PopupCardPlus} from "@/components/Plus/PopupCardPlus";
import Placeholder from "@tiptap/extension-placeholder";
import FontSize from "tiptap-extension-font-size";
import {Indent} from "@/components/EditorV2/indent";
import Link from "@/components/EditorV2/extension-link/src";
import ImageT from "@tiptap/extension-image";
import Image from "@/components/EditorV2/imgResize";
import SearchNReplace from "@/components/EditorV2/Search/plugin";
import {fileToBase64, ImagePaste} from "@/components/EditorV2/ImagePaste";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Blockquote from "@tiptap/extension-blockquote";
import {FontFamily} from "@tiptap/extension-font-family";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import CodeBlockComponent from "@/components/EditorV2/CodeBlock3";
import {lowlight} from "lowlight";
import DeeplinkNode from "@/components/Deeplink/DeeplinkNode";
import {mergeAttributes} from "@tiptap/core";
import latexInline from "@/components/EditorV2/latexInline/latexInline";
import {InlineCodeMark} from "@/components/EditorV2/InlineCodeMark/InlineCodeMark";
import CodeInlie from '@tiptap/extension-code'

/**
 * 用来作一些editor相关的操作...
 */
const SimpleEditor = forwardRef((props, ref) => {
    useImperativeHandle(ref,()=>({
        onRemoveHighlight:onRemoveHighlight,
        onUpdateComment:onUpdateComment
    }))

    const mCallback = useRef<any>(null);

    const extensions = [
        StarterKit,
        ListItem,
        OrderedList.extend({
            addAttributes() {
                return {
                    ...this.parent?.(), // 保留原有的属性
                    blockHighlight: {
                        default: false,
                    },
                    noteText: {
                        default: ''
                    }
                };
            },
        }),
        BulletList.extend({
            addAttributes() {
                return {
                    ...this.parent?.(), // 保留原有的属性
                    blockHighlight: {
                        default: false,
                    },
                    noteText: {
                        default: ''
                    }
                };
            },
        }),
        // Export.configure(importConfig),
        Focus.configure({
            className: 'has-focus',
            mode: 'deepest',
        }),
        RenderCustomTable.configure({
            resizable: true,
            // allowTableNodeSelection: true,
        }),
        TableRow,
        TableHeader,
        CustomTableCell,
        Youtube.extend({
            addAttributes() {
                return {
                    ...this.parent?.(), // 保留原有的属性
                    highlightImgUrl: {
                        default: '',
                    },
                    noteText: {
                        default: ''
                    }
                };
            },
        }),
        ListKeymap,
        HardBreak.extend({

        }),

        latexBlock,
        UniqueID.configure({
            types: ['tableRow', 'heading', 'taskList', 'paragraph', 'image', 'blockquote', 'bulletList', 'orderedList', 'horizontalRule', 'youtube', 'table', 'highlight', 'latexBlock'],
            filterTransaction: transaction => {
                return !transaction.getMeta('preventUpdate')
            }
        }),
        Gapcursor,
        Text,
        TextStyle,
        Color,
        Heading.configure({
            levels: [1, 2, 3],
        }).extend({
            renderHTML({ node, HTMLAttributes }) {
                const level = node.attrs.level || 1; // 获取标题的等级
                const fold = node.attrs.fold || false; // 获取 fold 属性，默认为 false

                // 返回HTML结构：span + h标题
                return [
                    'div', // 包裹标题和span的容器
                    {
                        class: 'heading-container',
                        id: crypto.randomUUID()
                    },
                    [
                        'img', // 使用 img 元素
                        {
                            class: 'heading-image', // 添加自定义样式类
                            src: fold ? getImagePathWithThemeSvg("arrowRight") : getImagePathWithThemeSvg("arrowBottom"), // 设置 src 属性
                            alt: 'heading image', // 可设置 alt 文本描述
                        },
                    ],
                    [
                        `h${level}`,
                        mergeAttributes(HTMLAttributes),
                        0,
                    ],
                ];
            },
            parseHTML() {
                return [
                    {
                        tag: 'div.heading-container', // 选择符合条件的 HTML 标签
                        getAttrs(dom) {
                            const level = dom.querySelector('h1') ? 1 :
                                dom.querySelector('h2') ? 2 :
                                    dom.querySelector('h3') ? 3 : 1;
                            return { level };
                        },
                    },
                ];
            },
            // 在这里你可以添加插件
            addProseMirrorPlugins() {
                return [
                    // 确保每个新 heading 生成唯一 ID
                    new Plugin({
                        key: new PluginKey("headingIdPlugin"),
                        appendTransaction: (transactions, oldState, newState) => {
                            let tr = newState.tr;
                            let modified = false;
                            newState.doc.descendants((node, pos) => {
                                if (node.type.name === "heading" && !node.attrs.id) {
                                    const newId = crypto.randomUUID();
                                    tr = tr.setNodeMarkup(pos, undefined, {
                                        ...node.attrs,
                                        id: newId,
                                    });
                                    modified = true;
                                }
                            });

                            return modified ? tr : null;
                        },
                    }),

                    new Plugin({
                        props: {
                            handleDOMEvents: {
                                click: (view, event) => {
                                    const target = event.target;
                                    const targetElementArr = ['H1', 'H2', 'H3']
                                    if (targetElementArr.includes(target.tagName)) {
                                        document.dispatchEvent(new CustomEvent('headingClick', {
                                            detail: {
                                                id: target.getAttribute('data-id')
                                            },
                                        }))
                                    }
                                    if (target.tagName === 'STRONG' && targetElementArr.includes(target.parentNode.tagName)) {
                                        document.dispatchEvent(new CustomEvent('headingClick', {
                                            detail: {
                                                id: target.parentNode.getAttribute('data-id')
                                            },
                                        }))
                                    }
                                    //if---------
                                    if (target.tagName === 'IMG' && target.classList.contains('heading-image')) {
                                        const targetHeadingId = target.parentNode.querySelector('[data-fold]').getAttribute('id')
                                        const selectNodeNP = editorRef.current.$doc.querySelectorAll('heading', { id: targetHeadingId })[0]
                                        const { state, dispatch } = view;
                                        const { tr } = state;
                                        const { schema } = state;
                                        setTimeout(() => {
                                            // 改变图标的状态
                                            let tr = state.tr.setNodeMarkup(selectNodeNP.pos - 1, schema.nodes.heading, {
                                                ...selectNodeNP.attributes,
                                                fold: !selectNodeNP.attributes.fold
                                            });
                                            // 改变所属节点的隐藏显示状态
                                            // 1,确定节点所属部分
                                            let foldContentArr = []
                                            let startCompute = false
                                            state.doc.descendants((node, pos) => {
                                                if (node.type.name === 'heading' && node.attrs.id === targetHeadingId) {
                                                    startCompute = true;
                                                } else if (startCompute && node.type.name !== 'text') {
                                                    if (node.type.name !== 'heading' || (node.type.name === 'heading' && node.attrs.level > selectNodeNP.attributes.level)) {
                                                        foldContentArr.push({
                                                            node: node,
                                                            pos: pos
                                                        })
                                                    } else {
                                                        startCompute = false;
                                                    }
                                                }
                                            });
                                            // 2,改变节点所属部分的显示状态
                                            foldContentArr.forEach((ele) => {
                                                tr.setNodeMarkup(ele.pos, schema.nodes[`${ele.node.type.name}`], {
                                                    ...ele.node.attrs,
                                                    ifhidden: selectNodeNP.attributes.fold ? false : true
                                                });
                                            })
                                            // tr = tr.setNodeMarkup(paragraphNP.pos - 1, schema.nodes.paragraph, {
                                            //   ...paragraphNP.attributes,
                                            //   ifhidden: !paragraphNP.attributes.ifhidden
                                            // });
                                            dispatch(tr);
                                            selectNodeNP.attributes.fold = !selectNodeNP.attributes.fold

                                            // 失焦
                                            editor.commands.blur()
                                        }, 50);
                                    }

                                },
                            },
                        },
                    }),
                ];
            },
            addAttributes() {
                return {
                    ...this.parent?.(),
                    id: {
                        // default: crypto.randomUUID(),
                        // renderHTML: attributes => {
                        //   if (!attributes['id']) {
                        //     return {};
                        //   }
                        //   return {
                        //     'id': attributes.id,
                        //   };
                        // },
                        default: () => {
                            const bb = crypto.randomUUID()
                            return null
                        },  // 生成唯一的 ID，只有第一次创建时生成
                        parseHTML: element => {
                            const aa = element?.lastElementChild?.getAttribute('id') || crypto.randomUUID()
                            // return element?.lastElementChild?.getAttribute('id') || crypto.randomUUID(); // 如果没有 id，从 HTML 中获取
                            return aa; // 如果没有 id，从 HTML 中获取
                        },
                    },
                    fold: {
                        default: false,
                        renderHTML: attributes => {
                            if (attributes.fold === undefined) {
                                return {};
                            }
                            return {
                                'data-fold': attributes.fold,
                            };
                        },
                    },
                    ifhidden: {
                        default: false,
                        renderHTML: attributes => {
                            if (attributes.ifhidden === undefined) {
                                return {};
                            }
                            return {
                                'ifhidden': attributes.ifhidden,
                            };
                        },
                    },
                };
            }
        }),
        Paragraph,
        Highlight.configure({ multicolor: true }).extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    id: {
                        default: crypto.randomUUID()
                    },
                    noteText: {
                        default: '',
                    },
                };
            },
            addCommands() {
                return {
                    ...this.parent?.(),
                    setHighlightId: (id,from) =>async  ({ tr, state, dispatch }) => {
                        await sleep(30)
                        const resolvedPos = this.editor.state.doc.resolve(from);
                        // 获取当前节点  实际id已经改变了，只是渲染的id值没变 重新渲染即可
                        const currentNode = resolvedPos.node().content.content[0];
                        currentNode.marks[0].attrs={...currentNode.marks[0].attrs,id:id}
                        return true;
                    }
                };
            }
        }),
        TextAlign.configure({
            types: ["heading", "paragraph"],
        }),
        Underline,
        getBlockMenu(),
        PopupCardPlus,
        Placeholder.configure({
            placeholder: '',
        }),
        FontSize,
        Indent,
        Link.configure({
            protocols: ['noteey'],
            autolink: false,
            linkOnPaste: false,
        }),
        ImageT,
        // Background,
        Image.configure({
            allowBase64: true
        }),
        SearchNReplace.configure({
            searchResultClass: "search-result", // class to give to found items. default 'search-result'
            caseSensitive: false, // no need to explain
            disableRegex: false, // also no need to explain
        }),
        ImagePaste.configure({
            fileMatchRegex: /^image\/(gif|jpe?g|a?png|svg|webp|bmp)/i,
            disableImagePaste: false,
            render: () => {
                return {
                    // onImagePaste: files => {
                    //     files.forEach(file => {
                    //         fileToBase64(file).then(r => {
                    //             const id = crypto.randomUUID();
                    //             model.addImg(id, r)
                    //             mEditor.current?.chain().focus().setImage({src: 'atom://' + id + '.jpeg'}).run()
                    //             mEditor.current?.commands.blur()
                    //         })
                    //     });
                    // },
                    // onDisabledImagePaste: text => {
                    //     console.log('text: ', text);
                    //     // add text to editor if you want, or display an error/upselll message
                    // },
                    // onImageDrop: files => {
                    //     files.forEach(file => {
                    //         fileToBase64(file).then(r => {
                    //             const id = crypto.randomUUID();
                    //             model.addImg(id, r)
                    //             mEditor.current?.chain().focus().setImage({src: 'atom://' + id + '.jpeg'}).run()
                    //             mEditor.current?.commands.blur()
                    //         })
                    //     });
                    // },
                };
            },
        }),
        TaskList.extend({
            addAttributes() {
                return {
                    ...this.parent?.(), // 保留原有的属性
                    blockHighlight: {
                        default: false,
                    },
                    noteText: {
                        default: ''
                    }
                };
            },
        }),
        TaskItem.configure({
            nested: true,
        }),
        HorizontalRule,
        Blockquote,
        // Hyperlink.configure({}),
        FontFamily,
        CodeBlockLowlight.extend({
            addNodeView() {
                return ReactNodeViewRenderer(CodeBlockComponent);
            },
        }).configure({lowlight}),
        DeeplinkNode.configure({
            fontSize: '13px'
        }),
        // InlineCodeMark,
        CodeInlie,
        latexInline,
    ]
    const editor = useEditor({
        injectCSS: true,
        extensions:extensions,
        parseOptions: {
                    preserveWhitespace: 'full',
                },
    });

    /**
     * 移除一个标注
     * @param highlight
     */
    const onRemoveHighlight = async (highlight:any,callback:any)=>{
        mCallback.current = callback;

        const card:any = await getCardById(highlight.libraryId);
        if (!card)return;
        editor?.commands.setContent(card.doc);

        // 获取当前选中节点
        // let from  = highlight.pos;
        // let to = highlight.end;
        // 重选选中区域
        // editor?.chain().focus().setTextSelection({ from: from, to: to }).run()
        // editor?.commands.unsetHighlight();
        if (highlight.annoType == 'image'){
            const selectedNP = editor?.$doc.querySelectorAll('image', {id: highlight.id})[0]
            if (selectedNP){
                selectedNP.node.attrs.highlightImgUrl = ""
                // document.dispatchEvent(new CustomEvent('changeHighlight'));
                // document.dispatchEvent(new CustomEvent('refreshSidebar'));
            }
        }else{
            editor?.chain()
                .focus()
                .setTextSelection({from: highlight.pos, to: highlight.end})  // 设置文本选择范围
                .unsetHighlight()
                .run();
        }

        await saveToCard(card)
    }

    /**
     * 更新评论...
     * @param highlight
     * @param callback
     */
    const onUpdateComment = async (highlight:any,callback:any)=>{
        mCallback.current = callback;
        const card:any = await getCardById(highlight.libraryId);
        if (!card)return;
        editor?.commands.setContent(card.doc);

        const highlights = await getAllHighlightContent(editor);
        const item = highlights.content.find(p=>p.id == highlight.id);
        if (item){
            item.node.attrs.noteText = highlight.comment;
            //保存card...
            await saveToCard(card);
        }
    }

    /**
     * 保存原始的card 到数据库
     * @param card
     */
    const saveToCard = async (card:any)=>{
        if (!card)return;
        //重新保存carddoc
        const html = imgServer.parserImgFromHtml(editor?.getHTML() as string);
        card.doc = html;
        await updateCard(card);
        if (mCallback.current){
            mCallback.current();
        }
    }
    return(
        <>
            {
                editor &&
                <EditorContent className="tableStyle"
                               editor={editor}/>
            }

        </>

    )
})

export default SimpleEditor;